import { VCPM } from '../models/project.model';
import { VersionUtils } from '../adapters/version-utils';

export interface StorageStretchDependency {
  domainId: number | undefined;
  domainName: string;
  workloadId: string | undefined;
  workloadName: string;
  computeClusterId: string | undefined;
  computeClusterName: string;
}

/** Canonical compute is domain-scoped; storage references are project-wide. */
export function getStorageStretchDependencies(data: VCPM.ProjectData | undefined, storageId: string): StorageStretchDependency[] {
  if (!storageId) return [];
  return (data?.workloadDomains ?? []).flatMap(domain => (domain.workloadList ?? []).flatMap(workload => {
    if (workload.vcfDeploymentInputs?.deploymentType !== 'dHCI' || workload.storageCluster?.id !== storageId) return [];
    const compute = domain.clusterConfigurations?.find(c => c.id === workload.computeCluster?.id);
    if ((compute?.clusterConfiguration?.clusterType ?? workload.computeClusterConfiguration?.clusterType) !== 'stretched') return [];
    return [{
      domainId: domain.id, domainName: domain.domainName,
      workloadId: workload.id, workloadName: workload.workload?.workloadName ?? workload.id ?? 'New workload',
      computeClusterId: workload.computeCluster?.id,
      computeClusterName: compute?.name ?? workload.computeCluster?.name ?? 'Compute cluster',
    }];
  }));
}

/** Overlay a draft without mutating saved data, including its shared compute topology. */
export function previewStorageStretchData(data: VCPM.ProjectData | undefined, domainId: number | undefined, draft: VCPM.Workload): VCPM.ProjectData {
  const domains = data?.workloadDomains ?? [];
  const savedDomain = domains.find(d => d.id === domainId);
  const domain = {
    ...savedDomain, id: domainId, domainName: savedDomain?.domainName ?? '',
    workloadList: [...(savedDomain?.workloadList ?? []).filter(w => draft.id == null || w.id !== draft.id), draft],
    clusterConfigurations: savedDomain?.clusterConfigurations?.map(c => c.id === draft.computeCluster?.id
      ? { ...c, clusterConfiguration: { ...c.clusterConfiguration, clusterType: (draft.vcfDeploymentInputs?.deploymentType === 'dHCI' ? draft.computeClusterConfiguration : draft.clusterConfiguration)?.clusterType ?? c.clusterConfiguration.clusterType } }
      : c),
  } as VCPM.WorkloadDomain;
  return { ...data, workloadDomains: [...domains.filter(d => d.id !== domainId), domain] };
}

/** Reconcile an owned candidate, comparing dependency transitions against the original project. */
export function reconcileStorageStretch(project: VCPM.Project, before?: VCPM.ProjectData): void {
  const data = project.data;
  const ids = new Set([
    ...(data.clusterConfigurations ?? []).map(c => c.id),
    ...(data.workloadDomains ?? []).flatMap(d => (d.workloadList ?? []).filter(w => w.vcfDeploymentInputs?.deploymentType === 'dHCI').map(w => w.storageCluster?.id).filter((id): id is string => !!id)),
  ]);
  for (const id of ids) {
    const required = getStorageStretchDependencies(data, id).length > 0;
    const released = !required && getStorageStretchDependencies(before, id).length > 0;
    const entity = data.clusterConfigurations?.find(c => c.id === id);
    const workloads = (data.workloadDomains ?? []).flatMap(d => d.workloadList ?? []).filter(w => w.vcfDeploymentInputs?.deploymentType === 'dHCI' && w.storageCluster?.id === id);
    const source = entity?.clusterConfiguration ?? workloads.find(w => w.storageClusterConfiguration)?.storageClusterConfiguration;
    if (!source) continue;
    const config = { ...source, clusterType: required ? 'stretched' : released ? 'standard' : source.clusterType };
    if (config.clusterType === 'stretched') {
      config.operationsReserve = false;
      config.hostRebuildReserve = false;
    } else if (released) {
      config.operationsReserve = true;
      config.hostRebuildReserve = false;
    }
    if (entity) entity.clusterConfiguration = config;
    for (const workload of workloads) {
      workload.storageClusterConfiguration = { ...config };
      if (config.clusterType === 'stretched' && workload.storageConfiguration?.vsanArchitecture?.toLowerCase() === 'esa'
        && VersionUtils.isVsanEsaDedupSupported(project.metadata?.version ?? '9.0.0')) {
        if (workload.clusterConfiguration) workload.clusterConfiguration.storageEfficiencyPreference = 'compressionOnly';
        if (workload.workloadDetails) {
          delete workload.workloadDetails.dedup;
          workload.workloadDetails.compression = workload.workloadDetails.dataReductionRatio ?? workload.workloadDetails.compression;
        }
      }
    }
  }
}

export function getStorageStretchErrors(data: VCPM.ProjectData | undefined): string[] {
  const errors = new Set<string>();
  for (const domain of data?.workloadDomains ?? []) {
    for (const workload of domain.workloadList ?? []) {
      if (workload.vcfDeploymentInputs?.deploymentType !== 'dHCI') continue;
      const id = workload.storageCluster?.id;
      const required = id ? getStorageStretchDependencies(data, id).length > 0 : workload.computeClusterConfiguration?.clusterType === 'stretched';
      const entity = id ? data?.clusterConfigurations?.find(c => c.id === id) : undefined;
      if (required && (workload.storageClusterConfiguration?.clusterType !== 'stretched' || (entity && entity.clusterConfiguration?.clusterType !== 'stretched'))) {
        errors.add(`Storage cluster ${entity?.name ?? workload.storageCluster?.name ?? 'in ' + domain.domainName} must be stretched because connected compute is stretched. Edit and save the workload configuration before sizing.`);
      }
    }
  }
  return [...errors];
}
